/**
 * Panel de publicacion entre tiendas.
 *
 * Una tienda participa de un job en dos roles distintos y no intercambiables:
 * como **origen** (de aca se clona el item) y como **destino** (aca se crea el
 * item nuevo). El rollup los muestra separados porque mezclarlos daria un total
 * que no significa nada: los mismos jobs contados dos veces cuando el origen y
 * el destino son tiendas que el usuario ve las dos.
 *
 * `needs_verification` no es "un fallo mas" y por eso sale del bloque de
 * numeros: son jobs que hablaron con MercadoLibre y no se sabe como terminaron
 * (el worker murio entre el POST y el commit). No se reintentan solos, para no
 * publicar un duplicado, asi que quedan ahi hasta que una persona mire la
 * cuenta real.
 *
 * Cuando no hay ningun job, el panel NO dibuja seis tarjetas en cero: dice que
 * la tabla esta vacia para esta tienda y que la llena encolar una publicacion.
 */

import type { PublicationCounts, StoreSummary } from "./api";
import { formatCount, formatDateTime, formatRelative } from "./format";
import { Note, Panel } from "./ui";

function count(counts: PublicationCounts, status: string): number {
  return counts.by_status[status] ?? 0;
}

/** Un rol (origen o destino) con sus numeros. Se dibuja solo si tiene jobs. */
function RoleCard({
  title,
  explanation,
  counts,
}: {
  readonly title: string;
  readonly explanation: string;
  readonly counts: PublicationCounts;
}) {
  return (
    <div className="home-role">
      <span className="home-role__title">{title}</span>
      <span className="home-role__hint">{explanation}</span>
      <dl className="home-role__grid">
        <div>
          <dt>Jobs</dt>
          <dd>{formatCount(counts.total)}</dd>
        </div>
        <div>
          <dt>Publicados</dt>
          <dd>{formatCount(count(counts, "succeeded"))}</dd>
        </div>
        <div>
          <dt>En curso</dt>
          <dd>{formatCount(counts.in_flight)}</dd>
        </div>
        <div>
          <dt>Fallidos</dt>
          <dd>{formatCount(count(counts, "failed"))}</dd>
        </div>
        <div>
          <dt>Omitidos</dt>
          <dd>{formatCount(count(counts, "skipped"))}</dd>
        </div>
        <div>
          <dt>A verificar</dt>
          <dd className={counts.needs_verification > 0 ? "home-role__alert" : undefined}>
            {formatCount(counts.needs_verification)}
          </dd>
        </div>
      </dl>
      {counts.last_activity_at === null ? null : (
        <p className="home-role__foot">
          Ultima actividad {formatRelative(counts.last_activity_at)} (
          {formatDateTime(counts.last_activity_at)})
          {counts.last_batch_id === null ? null : (
            <>
              {" "}
              &middot; corrida <code>{counts.last_batch_id}</code>
            </>
          )}
        </p>
      )}
    </div>
  );
}

export function PublicationPanel({ summary }: { readonly summary: StoreSummary }) {
  const asSource = summary.publication_as_source;
  const asTarget = summary.publication_as_target;
  const pendingVerification = asSource.needs_verification + asTarget.needs_verification;
  const empty = asSource.total === 0 && asTarget.total === 0;

  return (
    <Panel
      title="Publicacion entre tiendas"
      to="/publicaciones"
      toLabel="Ver publicaciones"
      lead={
        <>
          Rollup de <code>publication_jobs</code>. Sale de la base, no de la API
          de control de Celery: un job sigue teniendo respuesta despues de que su
          worker se reinicio.
        </>
      }
    >
      {empty ? (
        <Note>
          No hay ningun job registrado para esta tienda, ni como origen ni como
          destino. La tabla se llena al encolar una publicacion, tanto desde la
          seccion Publicacion como desde{" "}
          <code>scripts/tasks/enqueue_publication.py</code>: los dos caminos
          pasan por la misma task del worker.
        </Note>
      ) : (
        <>
          {pendingVerification > 0 ? (
            <Note tone="danger">
              <strong>{formatCount(pendingVerification)} publicaciones sin confirmar.</strong>{" "}
              Se hablo con MercadoLibre y no se sabe como termino. No se
              reintentan solas para no duplicar el item: hay que mirar la cuenta
              de destino.
            </Note>
          ) : null}

          <div className="home-roles">
            <RoleCard
              title="Como origen"
              explanation="Items de esta tienda que se clonaron hacia otra."
              counts={asSource}
            />
            <RoleCard
              title="Como destino"
              explanation="Items que se crearon en esta tienda clonando otra."
              counts={asTarget}
            />
          </div>
        </>
      )}
    </Panel>
  );
}
